import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, Package, ShoppingCart, MessageSquare, Hammer, Megaphone, IndianRupee, Users, TrendingUp, UserCheck, Copy } from "lucide-react";
import { api, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "orders", label: "Orders", icon: ShoppingCart },
  { key: "products", label: "Products", icon: Package },
  { key: "enquiries", label: "Enquiries", icon: MessageSquare },
  { key: "customizations", label: "Customizations", icon: Hammer },
  { key: "vendors", label: "Vendor Apps", icon: UserCheck },
  { key: "announcements", label: "Announcements", icon: Megaphone },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (!loading && (!user?.user_id || user.role !== "admin")) navigate("/");
  }, [user, loading, navigate]);

  if (loading || !user?.user_id) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;
  if (user.role !== "admin") return null;

  return (
    <div className="mx-auto max-w-[1400px] px-4 md:px-8 py-8">
      <h1 className="text-3xl font-extrabold text-midnight mb-1">Admin Panel</h1>
      <p className="text-sm text-muted-foreground mb-6">KRVMart management console</p>
      <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-8">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
            className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold ${tab === t.key ? "bg-midnight text-white" : "border border-border text-muted-foreground hover:text-midnight"}`}>
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "dashboard" && <Dashboard />}
      {tab === "orders" && <Orders />}
      {tab === "products" && <Products />}
      {tab === "enquiries" && <Enquiries />}
      {tab === "customizations" && <Customizations />}
      {tab === "vendors" && <VendorApps />}
      {tab === "announcements" && <Announcements />}
    </div>
  );
}

function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <p className="text-muted-foreground">Loading…</p>;
  const cards = [
    { icon: IndianRupee, label: "Revenue", value: inr(s.revenue) },
    { icon: ShoppingCart, label: "Orders", value: s.orders },
    { icon: TrendingUp, label: "Avg Order Value", value: inr(s.avg_order_value) },
    { icon: Package, label: "Products", value: s.products },
    { icon: Users, label: "Customers", value: s.customers },
    { icon: MessageSquare, label: "Enquiries", value: s.enquiries },
    { icon: Hammer, label: "Custom Requests", value: s.customizations },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-dashboard">
      {cards.map((c) => (
        <div key={c.label} className="rounded-3xl border border-border p-6">
          <c.icon className="h-6 w-6 text-gold-dark" />
          <p className="mt-3 text-2xl font-extrabold text-midnight">{c.value}</p>
          <p className="text-sm text-muted-foreground">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

function Orders() {
  const [orders, setOrders] = useState([]);
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, field, value) => { await api.post(`/admin/orders/${id}/status`, { [field]: value }); toast.success("Updated"); load(); };
  return (
    <div className="space-y-3" data-testid="admin-orders">
      {orders.length === 0 && <p className="text-muted-foreground">No orders yet.</p>}
      {orders.map((o) => (
        <div key={o.id} className="rounded-2xl border border-border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><p className="font-bold text-midnight">{o.order_number}</p><p className="text-xs text-muted-foreground">{o.address?.name} • {inr(o.grand_total)} • {o.payment_method}</p></div>
            <div className="flex gap-2">
              <select value={o.status} onChange={(e) => update(o.id, "status", e.target.value)} data-testid={`admin-order-status-${o.order_number}`} className="rounded-full border border-border px-3 py-2 text-sm">
                {["Confirmed", "Shipped", "Delivered", "Cancelled"].map((s) => <option key={s}>{s}</option>)}
              </select>
              {o.installation_status && (
                <select value={o.installation_status} onChange={(e) => update(o.id, "installation_status", e.target.value)} className="rounded-full border border-border px-3 py-2 text-sm">
                  {["Pending", "Scheduled", "Completed", "Confirmed"].map((s) => <option key={s}>Install: {s}</option>)}
                </select>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function Products() {
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({ name: "", mart: "home-mart", category: "", brand: "", price: "", mrp: "", images: "", description: "", stock: 10, product_type: "standard" });
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.price || !form.brand) { toast.error("Name, brand and price required"); return; }
    await api.post("/admin/products", { ...form, price: Number(form.price), mrp: form.mrp ? Number(form.mrp) : null, images: form.images ? [form.images] : [], stock: Number(form.stock) });
    toast.success("Product added"); setAdding(false); setForm({ ...form, name: "", price: "", mrp: "", images: "", description: "" }); load();
  };
  const del = async (id) => { await api.delete(`/admin/products/${id}`); load(); };

  return (
    <div data-testid="admin-products">
      <button onClick={() => setAdding(!adding)} data-testid="admin-add-product-btn" className="mb-4 rounded-full bg-midnight px-5 py-2.5 text-sm font-bold text-white">{adding ? "Cancel" : "+ Add Product"}</button>
      {adding && (
        <div className="mb-6 grid sm:grid-cols-2 gap-3 rounded-3xl border border-border p-6">
          {[["name", "Name"], ["brand", "Brand"], ["category", "Category"], ["price", "Price"], ["mrp", "MRP (optional)"], ["stock", "Stock"], ["images", "Image URL"], ["description", "Description"]].map(([k, ph]) => (
            <input key={k} value={form[k]} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))} placeholder={ph} data-testid={`admin-product-${k}`} className="rounded-xl border border-border px-4 py-3 text-sm outline-none focus:border-gold" />
          ))}
          <select value={form.mart} onChange={(e) => setForm((f) => ({ ...f, mart: e.target.value }))} className="rounded-xl border border-border px-4 py-3 text-sm">
            {["home-mart", "style-mart", "wood-mart", "tech-mart"].map((m) => <option key={m}>{m}</option>)}
          </select>
          <button onClick={create} data-testid="admin-save-product-btn" className="rounded-full bg-gold py-3 font-bold text-midnight sm:col-span-2">Save Product</button>
        </div>
      )}
      <div className="space-y-2">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-3 rounded-2xl border border-border p-3">
            <img src={p.images?.[0]} alt="" className="h-12 w-12 rounded-lg object-cover bg-secondary" />
            <div className="flex-1"><p className="text-sm font-bold text-midnight line-clamp-1">{p.name}</p><p className="text-xs text-muted-foreground">{p.mart} • {p.brand} • {inr(p.price)} • stock {p.stock}</p></div>
            <button onClick={() => del(p.id)} data-testid={`admin-delete-${p.slug}`} className="text-sm font-semibold text-red-600">Delete</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Enquiries() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/enquiries").then((r) => setList(r.data)); }, []);
  return (
    <div className="space-y-3" data-testid="admin-enquiries">
      {list.length === 0 && <p className="text-muted-foreground">No enquiries yet.</p>}
      {list.map((e) => (
        <div key={e.id} className="rounded-2xl border border-border p-4">
          <div className="flex items-center justify-between"><span className="rounded-full bg-secondary px-3 py-1 text-xs font-bold uppercase">{e.type}</span><span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString("en-IN")}</span></div>
          <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            {Object.entries(e.data).map(([k, v]) => <p key={k}><span className="text-muted-foreground">{k}:</span> <span className="font-semibold text-midnight">{v}</span></p>)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Customizations() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/customizations").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const update = async (id, payload) => { await api.post(`/admin/customizations/${id}`, payload); toast.success("Updated"); load(); };
  return (
    <div className="space-y-3" data-testid="admin-customizations">
      {list.length === 0 && <p className="text-muted-foreground">No customization requests yet.</p>}
      {list.map((c) => (
        <div key={c.id} className="rounded-2xl border border-border p-4">
          <p className="text-sm text-midnight"><b>{c.name}</b> • {c.phone}</p>
          <p className="text-sm text-muted-foreground mt-1">{c.details} {c.finish && `• Finish: ${c.finish}`}</p>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <select value={c.status} onChange={(e) => update(c.id, { status: e.target.value })} className="rounded-full border border-border px-3 py-2 text-sm">
              {["Awaiting Vendor Confirmation", "Quote Sent", "Approved", "Confirmed", "Cancelled"].map((s) => <option key={s}>{s}</option>)}
            </select>
            <input placeholder="Quote ₹" type="number" defaultValue={c.quote || ""} onBlur={(e) => e.target.value && update(c.id, { quote: Number(e.target.value), status: "Quote Sent" })} className="w-32 rounded-full border border-border px-3 py-2 text-sm" />
          </div>
        </div>
      ))}
    </div>
  );
}

function VendorApps() {
  const [apps, setApps] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [links, setLinks] = useState({});
  const load = () => {
    api.get("/admin/enquiries").then((r) => setApps(r.data.filter((e) => ["furniture_vendor", "vendor"].includes(e.type))));
    api.get("/admin/vendors").then((r) => setVendors(r.data));
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/admin/enquiries/${id}/approve-vendor`);
      const link = window.location.origin + data.onboarding_path;
      setLinks((l) => ({ ...l, [id]: link }));
      toast.success("Vendor approved — share the onboarding link");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const copy = (link) => { navigator.clipboard.writeText(link); toast.success("Onboarding link copied"); };

  return (
    <div data-testid="admin-vendor-apps">
      <h2 className="text-lg font-extrabold text-midnight mb-3">Vendor Applications</h2>
      {apps.length === 0 && <p className="text-muted-foreground mb-6">No vendor applications yet.</p>}
      <div className="space-y-3 mb-10">
        {apps.map((a) => (
          <div key={a.id} className="rounded-2xl border border-border p-4" data-testid={`vendor-app-${a.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-bold text-midnight">{a.data.business_name || a.data.company || "Vendor"}</p>
                <p className="text-xs text-muted-foreground">{a.data.email} • {a.data.phone} • {a.data.product_categories || a.data.categories || ""}</p>
              </div>
              {a.status === "Approved" ? <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-700">Approved</span>
                : <button onClick={() => approve(a.id)} data-testid={`approve-vendor-${a.id}`} className="rounded-full bg-mart-wood px-5 py-2 text-sm font-bold text-white">Approve & Onboard</button>}
            </div>
            {links[a.id] && (
              <div className="mt-3 flex items-center gap-2 rounded-xl bg-secondary p-3">
                <input readOnly value={links[a.id]} className="flex-1 bg-transparent text-xs outline-none" data-testid={`onboard-link-${a.id}`} />
                <button onClick={() => copy(links[a.id])} className="flex items-center gap-1 rounded-full bg-midnight px-3 py-1.5 text-xs font-bold text-white"><Copy className="h-3 w-3" /> Copy</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <h2 className="text-lg font-extrabold text-midnight mb-3">Active Vendors</h2>
      <div className="grid sm:grid-cols-2 gap-3">
        {vendors.map((v) => (
          <div key={v.vendor_id} className="rounded-2xl border border-border p-4">
            <p className="font-bold text-midnight">{v.name}</p>
            <p className="text-xs text-muted-foreground">{v.city} • {v.status}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function Announcements() {
  const [text, setText] = useState("");
  useEffect(() => { api.get("/announcements").then((r) => setText(r.data.join("\n"))); }, []);
  const save = async () => { await api.put("/admin/announcements", { announcements: text.split("\n").map((s) => s.trim()).filter(Boolean) }); toast.success("Announcements updated — refresh to see in header"); };
  return (
    <div className="max-w-xl" data-testid="admin-announcements">
      <p className="text-sm text-muted-foreground mb-3">One announcement per line. Shown in the top bar marquee.</p>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={6} className="w-full rounded-2xl border border-border p-4 text-sm outline-none focus:border-gold" />
      <button onClick={save} data-testid="admin-save-announcements" className="mt-3 rounded-full bg-midnight px-6 py-3 font-bold text-white">Save</button>
    </div>
  );
}
